import moment from 'moment-jalaali';
import 'moment-jalaali';

/**
 * dateUtils.js - سیستم مرکزی مدیریت تاریخ
 * 
 * این فایل شامل توابع کاربردی برای تبدیل، فرمت‌دهی و اعتبارسنجی تاریخ‌ها است.
 * از moment-jalaali برای تبدیل بین تقویم میلادی و جلالی استفاده می‌کند.
 */

/**
 * تبدیل تاریخ به فرمت جلالی (string)
 * 
 * @param {string|Date|Object} date - تاریخ ورودی (میلادی، جلالی، یا Date object)
 * @param {string} format - فرمت خروجی (پیش‌فرض: 'jYYYY/jMM/jDD')
 * @returns {string|null} - تاریخ جلالی فرمت شده یا null در صورت خطا
 * 
 * @example
 * toJalali('2024-01-15') // '1402/10/25'
 * toJalali(new Date()) // تاریخ امروز به جلالی
 * toJalali('2024-01-15', 'jYYYY-jMM-jDD') // '1402-10-25'
 */
export const toJalali = (date, format = 'jYYYY/jMM/jDD') => {
  if (!date) return null;

  try {
    let m;

    // اگر رشته میلادی باشد (YYYY-MM-DD یا ISO datetime)
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      m = date.includes('T') ? moment(date) : moment(date, 'YYYY-MM-DD');
    }
    // اگر رشته جلالی باشد
    else if (typeof date === 'string' && /^\d{4}\/\d{2}\/\d{2}/.test(date)) {
      m = moment(date, 'jYYYY/jMM/jDD');
    }
    // اگر Date object باشد
    else if (date instanceof Date) {
      m = moment(date);
    }
    // اگر DateObject از react-multi-date-picker باشد
    else if (date?.toDate && typeof date.toDate === 'function') {
      m = moment(date.toDate());
    }
    // اگر DateObject با jYear, jMonth, jDay باشد
    else if (date?.jYear !== undefined && date?.jMonth !== undefined && date?.jDay !== undefined) {
      m = moment(`${date.jYear}/${date.jMonth + 1}/${date.jDay}`, 'jYYYY/jMM/jDD');
    }
    // اگر moment object باشد
    else if (moment.isMoment(date)) {
      m = date;
    }
    // تلاش برای parse کردن
    else {
      m = moment(date);
    }

    if (!m || !m.isValid()) {
      return null;
    }

    return m.format(format);
  } catch (error) {
    console.error('Error converting to Jalali:', error);
    return null;
  }
};

/**
 * تبدیل تاریخ به فرمت میلادی (YYYY-MM-DD)
 * 
 * @param {string|Date|Object} date - تاریخ ورودی (جلالی، میلادی، یا Date object)
 * @param {string} format - فرمت خروجی (پیش‌فرض: 'YYYY-MM-DD')
 * @returns {string|null} - تاریخ میلادی فرمت شده یا null در صورت خطا
 * 
 * @example
 * toGregorian('1402/10/25') // '2024-01-15'
 * toGregorian(new Date()) // تاریخ امروز به میلادی
 * toGregorian('1402/10/25', 'YYYY/MM/DD') // '2024/01/15'
 */
export const toGregorian = (date, format = 'YYYY-MM-DD') => {
  if (!date) return null;

  try {
    let m;

    // اگر رشته میلادی باشد (YYYY-MM-DD)
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      m = moment(date, 'YYYY-MM-DD');
    }
    // اگر رشته جلالی باشد
    else if (typeof date === 'string' && /^\d{4}\/\d{2}\/\d{2}/.test(date)) {
      m = moment(date, 'jYYYY/jMM/jDD');
    }
    // اگر Date object باشد
    else if (date instanceof Date) {
      m = moment(date);
    }
    // اگر DateObject از react-multi-date-picker باشد
    else if (date?.toDate && typeof date.toDate === 'function') {
      m = moment(date.toDate());
    }
    // اگر DateObject با jYear, jMonth, jDay باشد
    else if (date?.jYear !== undefined && date?.jMonth !== undefined && date?.jDay !== undefined) {
      m = moment(`${date.jYear}/${date.jMonth + 1}/${date.jDay}`, 'jYYYY/jMM/jDD');
    }
    // اگر moment object باشد
    else if (moment.isMoment(date)) {
      m = date;
    }
    // تلاش برای parse کردن
    else {
      m = moment(date);
    }

    if (!m || !m.isValid()) {
      return null;
    }

    return m.format(format);
  } catch (error) {
    console.error('Error converting to Gregorian:', error);
    return null;
  }
};

/**
 * فرمت‌دهی تاریخ با فرمت دلخواه
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @param {string} format - فرمت خروجی (میلادی یا جلالی)
 * @param {string} calendar - نوع تقویم ('jalali' یا 'gregorian')
 * @returns {string|null} - تاریخ فرمت شده یا null در صورت خطا
 * 
 * @example
 * formatDate('2024-01-15', 'jYYYY/jMM/jDD', 'jalali') // '1402/10/25'
 * formatDate('1402/10/25', 'YYYY-MM-DD', 'gregorian') // '2024-01-15'
 * formatDate(new Date(), 'jYYYY/jMM/jDD', 'jalali') // تاریخ امروز به جلالی
 */
export const formatDate = (date, format = 'jYYYY/jMM/jDD', calendar = 'jalali') => {
  if (!date) return null;

  try {
    let m;

    // تشخیص نوع ورودی
    if (typeof date === 'string') {
      // اگر رشته میلادی باشد
      if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
        m = moment(date, 'YYYY-MM-DD');
      }
      // اگر رشته جلالی باشد
      else if (/^\d{4}\/\d{2}\/\d{2}/.test(date)) {
        m = moment(date, 'jYYYY/jMM/jDD');
      }
      // تلاش برای parse کردن
      else {
        m = moment(date);
      }
    } else if (date instanceof Date) {
      m = moment(date);
    } else if (date?.toDate && typeof date.toDate === 'function') {
      m = moment(date.toDate());
    } else if (date?.jYear !== undefined && date?.jMonth !== undefined && date?.jDay !== undefined) {
      m = moment(`${date.jYear}/${date.jMonth + 1}/${date.jDay}`, 'jYYYY/jMM/jDD');
    } else if (moment.isMoment(date)) {
      m = date;
    } else {
      m = moment(date);
    }

    if (!m || !m.isValid()) {
      return null;
    }

    // اگر فرمت جلالی باشد
    if (calendar === 'jalali' || format.startsWith('j')) {
      return m.format(format);
    }
    // اگر فرمت میلادی باشد
    return m.format(format);
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};

/**
 * اعتبارسنجی تاریخ
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @returns {boolean} - true اگر تاریخ معتبر باشد
 * 
 * @example
 * isValidDate('2024-01-15') // true
 * isValidDate('2024-13-45') // false
 * isValidDate('1402/10/25') // true
 */
export const isValidDate = (date) => {
  if (!date) return false;

  try {
    let m;

    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
        m = moment(date, 'YYYY-MM-DD');
      } else if (/^\d{4}\/\d{2}\/\d{2}/.test(date)) {
        m = moment(date, 'jYYYY/jMM/jDD');
      } else {
        m = moment(date);
      }
    } else if (date instanceof Date) {
      m = moment(date);
    } else if (date?.toDate && typeof date.toDate === 'function') {
      m = moment(date.toDate());
    } else if (date?.jYear !== undefined && date?.jMonth !== undefined && date?.jDay !== undefined) {
      m = moment(`${date.jYear}/${date.jMonth + 1}/${date.jDay}`, 'jYYYY/jMM/jDD');
    } else if (moment.isMoment(date)) {
      m = date;
    } else {
      m = moment(date);
    }

    return m && m.isValid();
  } catch (error) {
    return false;
  }
};

/**
 * مقایسه دو تاریخ
 * 
 * @param {string|Date|Object} date1 - تاریخ اول
 * @param {string|Date|Object} date2 - تاریخ دوم
 * @returns {number} - -1 اگر date1 قبل از date2 باشد، 0 اگر برابر باشند، 1 اگر date1 بعد از date2 باشد
 * 
 * @example
 * compareDates('2024-01-15', '2024-01-20') // -1
 * compareDates('2024-01-15', '2024-01-15') // 0
 * compareDates('2024-01-20', '2024-01-15') // 1
 */
export const compareDates = (date1, date2) => {
  if (!date1 || !date2) return null;

  try {
    const m1 = parseDate(date1);
    const m2 = parseDate(date2);

    if (!m1 || !m1.isValid() || !m2 || !m2.isValid()) {
      return null;
    }

    if (m1.isBefore(m2)) return -1;
    if (m1.isAfter(m2)) return 1;
    return 0;
  } catch (error) {
    console.error('Error comparing dates:', error);
    return null;
  }
};

/**
 * بررسی اینکه آیا تاریخ اول قبل از تاریخ دوم است
 * 
 * @param {string|Date|Object} date1 - تاریخ اول
 * @param {string|Date|Object} date2 - تاریخ دوم
 * @returns {boolean} - true اگر date1 قبل از date2 باشد
 */
export const isBefore = (date1, date2) => {
  const result = compareDates(date1, date2);
  return result === -1;
};

/**
 * بررسی اینکه آیا تاریخ اول بعد از تاریخ دوم است
 * 
 * @param {string|Date|Object} date1 - تاریخ اول
 * @param {string|Date|Object} date2 - تاریخ دوم
 * @returns {boolean} - true اگر date1 بعد از date2 باشد
 */
export const isAfter = (date1, date2) => {
  const result = compareDates(date1, date2);
  return result === 1;
};

/**
 * بررسی اینکه آیا دو تاریخ برابر هستند
 * 
 * @param {string|Date|Object} date1 - تاریخ اول
 * @param {string|Date|Object} date2 - تاریخ دوم
 * @returns {boolean} - true اگر دو تاریخ برابر باشند
 */
export const isEqual = (date1, date2) => {
  const result = compareDates(date1, date2);
  return result === 0;
};

/**
 * تابع کمکی برای parse کردن تاریخ به moment object
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @returns {moment.Moment|null} - moment object یا null
 */
const parseDate = (date) => {
  if (!date) return null;

  try {
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
        return moment(date, 'YYYY-MM-DD');
      } else if (/^\d{4}\/\d{2}\/\d{2}/.test(date)) {
        return moment(date, 'jYYYY/jMM/jDD');
      } else {
        return moment(date);
      }
    } else if (date instanceof Date) {
      return moment(date);
    } else if (date?.toDate && typeof date.toDate === 'function') {
      return moment(date.toDate());
    } else if (date?.jYear !== undefined && date?.jMonth !== undefined && date?.jDay !== undefined) {
      return moment(`${date.jYear}/${date.jMonth + 1}/${date.jDay}`, 'jYYYY/jMM/jDD');
    } else if (moment.isMoment(date)) {
      return date;
    } else {
      return moment(date);
    }
  } catch (error) {
    return null;
  }
};

/**
 * دریافت تاریخ امروز به فرمت جلالی
 * 
 * @param {string} format - فرمت خروجی (پیش‌فرض: 'jYYYY/jMM/jDD')
 * @returns {string} - تاریخ امروز به جلالی
 */
export const getTodayJalali = (format = 'jYYYY/jMM/jDD') => {
  return moment().format(format);
};

export const normalizeJalaliApiDate = (value) => {
  if (!value) return null;

  const normalized = String(value)
    .trim()
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/-/g, '/')
    .replace(/j(?=\d)/gi, '');

  const parts = normalized.split('/');
  if (parts.length !== 3) return normalized || null;

  const [year, month, day] = parts;
  return `${String(year).padStart(4, '0')}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
};

export const formatJalaliApiDate = (date) => {
  if (!date) return null;

  if (typeof date === 'string') {
    if (/^j?\d{4}[/-]j?\d{1,2}[/-]j?\d{1,2}$/i.test(date.trim())) {
      return normalizeJalaliApiDate(date);
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(date.trim())) {
      return normalizeJalaliApiDate(moment(date).format('jYYYY/jMM/jDD'));
    }
    return normalizeJalaliApiDate(date);
  }

  if (date?.format && typeof date.format === 'function') {
    return normalizeJalaliApiDate(date.format('jYYYY/jMM/jDD'));
  }

  if (date instanceof Date || moment.isMoment(date) || (date?.toDate && typeof date.toDate === 'function')) {
    const m = date?.toDate ? moment(date.toDate()) : moment(date);
    return normalizeJalaliApiDate(m.format('jYYYY/jMM/jDD'));
  }

  return normalizeJalaliApiDate(date);
};

/**
 * دریافت تاریخ امروز به فرمت میلادی
 * 
 * @param {string} format - فرمت خروجی (پیش‌فرض: 'YYYY-MM-DD')
 * @returns {string} - تاریخ امروز به میلادی
 */
export const getTodayGregorian = (format = 'YYYY-MM-DD') => {
  return moment().format(format);
};

/**
 * اضافه کردن روز/ماه/سال به تاریخ
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @param {number} amount - مقدار اضافه کردن
 * @param {string} unit - واحد ('days', 'months', 'years')
 * @param {string} format - فرمت خروجی
 * @param {string} calendar - نوع تقویم ('jalali' یا 'gregorian')
 * @returns {string|null} - تاریخ جدید یا null
 * 
 * @example
 * addToDate('2024-01-15', 7, 'days') // '2024-01-22'
 * addToDate('1402/10/25', 1, 'months', 'jYYYY/jMM/jDD', 'jalali') // '1402/11/25'
 */
export const addToDate = (date, amount, unit = 'days', format = 'YYYY-MM-DD', calendar = 'gregorian') => {
  if (!date) return null;

  try {
    const m = parseDate(date);
    if (!m || !m.isValid()) return null;

    const newDate = m.add(amount, unit);

    if (calendar === 'jalali' || format.startsWith('j')) {
      return newDate.format(format);
    }
    return newDate.format(format);
  } catch (error) {
    console.error('Error adding to date:', error);
    return null;
  }
};

/**
 * تفریق روز/ماه/سال از تاریخ
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @param {number} amount - مقدار تفریق
 * @param {string} unit - واحد ('days', 'months', 'years')
 * @param {string} format - فرمت خروجی
 * @param {string} calendar - نوع تقویم ('jalali' یا 'gregorian')
 * @returns {string|null} - تاریخ جدید یا null
 */
export const subtractFromDate = (date, amount, unit = 'days', format = 'YYYY-MM-DD', calendar = 'gregorian') => {
  return addToDate(date, -amount, unit, format, calendar);
};

/**
 * دریافت نام روز هفته به فارسی
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @returns {string|null} - نام روز هفته یا null
 */
export const getDayName = (date) => {
  if (!date) return null;

  try {
    const m = parseDate(date);
    if (!m || !m.isValid()) return null;

    const dayNames = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'];
    return dayNames[m.day()];
  } catch (error) {
    return null;
  }
};

/**
 * دریافت نام ماه به فارسی
 * 
 * @param {string|Date|Object} date - تاریخ ورودی
 * @param {string} calendar - نوع تقویم ('jalali' یا 'gregorian')
 * @returns {string|null} - نام ماه یا null
 */
export const getMonthName = (date, calendar = 'jalali') => {
  if (!date) return null;

  try {
    const m = parseDate(date);
    if (!m || !m.isValid()) return null;

    if (calendar === 'jalali') {
      const monthNames = [
        'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
        'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
      ];
      return monthNames[m.jMonth()];
    } else {
      const monthNames = [
        'ژانویه', 'فوریه', 'مارس', 'آوریل', 'می', 'ژوئن',
        'جولای', 'آگوست', 'سپتامبر', 'اکتبر', 'نوامبر', 'دسامبر'
      ];
      return monthNames[m.month()];
    }
  } catch (error) {
    return null;
  }
};

/**
 * دریافت بازه تاریخ 30 روز گذشته (از 30 روز پیش تا امروز)
 * 
 * @param {string} format - فرمت خروجی (پیش‌فرض: 'jYYYY/jMM/jDD')
 * @param {string} calendar - نوع تقویم ('jalali' یا 'gregorian')
 * @returns {Object} - { startDate, endDate } - تاریخ شروع و پایان
 * 
 * @example
 * getLast30DaysRange() // { startDate: '1403/10/01', endDate: '1403/10/30' }
 * getLast30DaysRange('YYYY-MM-DD', 'gregorian') // { startDate: '2024-01-01', endDate: '2024-01-30' }
 */
export const getLast30DaysRange = (format = 'jYYYY/jMM/jDD', calendar = 'jalali') => {
  const today = moment();
  const thirtyDaysAgo = moment().subtract(30, 'days');
  
  if (calendar === 'jalali' || format.startsWith('j')) {
    return {
      startDate: thirtyDaysAgo.format(format),
      endDate: today.format(format),
    };
  } else {
    return {
      startDate: thirtyDaysAgo.format(format),
      endDate: today.format(format),
    };
  }
};

export const getLast30DaysJalaliApiRange = () => ({
  startDate: formatJalaliApiDate(moment().subtract(30, 'days')),
  endDate: formatJalaliApiDate(moment()),
});

/**
 * دریافت تاریخ 30 روز گذشته به عنوان Date object برای PersianDatePicker
 * 
 * @returns {Date} - Date object برای استفاده در PersianDatePicker
 */
export const getLast30DaysAgoDate = () => {
  return moment().subtract(30, 'days').toDate();
};

/**
 * دریافت تاریخ امروز به عنوان Date object برای PersianDatePicker
 * 
 * @returns {Date} - Date object برای استفاده در PersianDatePicker
 */
export const getTodayDate = () => {
  return moment().toDate();
};

/**
 * نمایش تاریخ/زمان ISO برگشتی از API به شمسی
 *
 * @param {string|Date} value - مثال: 2026-05-11T22:54:08+03:30
 * @param {{ includeTime?: boolean, hideMidnight?: boolean }} options
 *   hideMidnight: اگر true باشد و ساعت واقعاً 00:00 باشد، فقط تاریخ نشان داده می‌شود
 * @returns {string|null}
 */
export const formatApiDateTimeJalali = (value, { includeTime = true, hideMidnight = false } = {}) => {
  if (value == null || value === '') return null;
  const m = moment(value);
  if (!m.isValid()) {
    const s = String(value).trim();
    return s || null;
  }
  const raw = String(value);
  const hasTimeComponent = /T|\d{1,2}:\d{2}/.test(raw);
  let showTime = includeTime && hasTimeComponent;
  if (showTime && hideMidnight) {
    const isMidnight = m.hour() === 0 && m.minute() === 0 && m.second() === 0;
    if (isMidnight) showTime = false;
  }
  const fmt = showTime ? 'jYYYY/jMM/jDD HH:mm' : 'jYYYY/jMM/jDD';
  return m.format(fmt);
};

/**
 * نمایش توصیفی تاریخ نسبت به امروز (مثلاً «هفته آینده»، «فردا»)
 */
export const formatDescriptiveDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = moment(dateString).startOf('day');
    if (!date.isValid()) return toJalali(dateString);
    const today = moment().startOf('day');
    const diffDays = date.diff(today, 'days');

    if (diffDays === 0) return 'امروز';
    if (diffDays === 1) return 'فردا';
    if (diffDays === -1) return 'دیروز';
    if (diffDays >= 2 && diffDays <= 6) return `${diffDays} روز دیگر`;
    if (diffDays >= 7 && diffDays <= 13) return 'هفته آینده';
    if (diffDays >= 14 && diffDays <= 20) return 'دو هفته آینده';
    if (diffDays >= 21 && diffDays <= 27) return 'سه هفته آینده';
    if (diffDays >= 28 && diffDays <= 34) return 'یک ماه آینده';
    if (diffDays <= -2 && diffDays >= -6) return `${Math.abs(diffDays)} روز پیش`;
    if (diffDays <= -7 && diffDays >= -13) return 'هفته پیش';
    if (diffDays <= -14 && diffDays >= -27) return 'یک ماه پیش';
    if (diffDays <= -365) return 'یک سال گذشته';

    return toJalali(dateString);
  } catch {
    return toJalali(dateString);
  }
};

/**
 * برچسب تاریخ بستن: توصیفی + تاریخ شمسی در صورت نیاز
 */
export const formatCloseDateLabel = (dateString) => {
  const descriptive = formatDescriptiveDate(dateString);
  const absolute = toJalali(dateString);
  if (!descriptive && !absolute) return null;
  if (descriptive && ['امروز', 'فردا', 'دیروز', 'هفته آینده', 'دو هفته آینده', 'سه هفته آینده', 'یک ماه آینده', 'هفته پیش', 'یک ماه پیش', 'یک سال گذشته'].includes(descriptive)) {
    return descriptive;
  }
  if (descriptive && descriptive.includes('روز')) return descriptive;
  return absolute || descriptive;
};